import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  IconButton,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import WarningAmber from '@mui/icons-material/WarningAmber';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Close from '@mui/icons-material/CloseOutlined';
import DoneAll from '@mui/icons-material/DoneAllOutlined';
import DeleteSweep from '@mui/icons-material/DeleteSweepOutlined';
import { Bell as BellLucide } from 'lucide-react';
import {
  useActionCenter,
  type ActionCenterNotification,
  type NotificationSeverity,
} from '@/hooks/useActionCenter';
import { useTranslation } from 'react-i18next';
import { entityDetailPathByName } from '@/lib/pageEntityContext';
import i18n from '@/i18n';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DRAWER_WIDTH = 380;

const severityIcon: Record<NotificationSeverity, React.ReactNode> = {
  success: <CheckCircleOutline fontSize="small" color="success" />,
  error: <ErrorOutline fontSize="small" color="error" />,
  warning: <WarningAmber fontSize="small" color="warning" />,
  info: <InfoOutlined fontSize="small" color="info" />,
};

/**
 * A notification carrying an entityType/entityId pair deep-links to that record. The path comes
 * from the generated navigation (entityDetailPathByName), so every entity with a page is
 * navigable — this used to be a hand-written map that covered three of them.
 */
function getNotificationRoute(notification: ActionCenterNotification): string | undefined {
  if (!notification.entityType || !notification.entityId) return undefined;
  return entityDetailPathByName(notification.entityType, notification.entityId);
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return i18n.t('notifications.justNow');
  if (diffMin < 60) return i18n.t('notifications.minutesAgo', { count: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return i18n.t('notifications.hoursAgo', { count: diffHr });
  return d.toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Notification Row
// ---------------------------------------------------------------------------

function NotificationRow({
  notification,
  onDismiss,
  onMarkRead,
  onNavigate,
}: {
  notification: ActionCenterNotification;
  onDismiss: (id: string) => void;
  onMarkRead: (id: string) => void;
  onNavigate: (route: string) => void;
}) {
  const route = getNotificationRoute(notification);
  return (
    <ListItem
      sx={{
        alignItems: 'flex-start',
        bgcolor: notification.read ? 'transparent' : 'action.hover',
        '&:hover': { bgcolor: 'action.selected' },
        py: 1,
        px: 1.5,
        cursor: route ? 'pointer' : 'default',
      }}
      secondaryAction={
        <IconButton
          edge="end"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(notification.id);
          }}
          title={i18n.t('notifications.dismiss')}
        >
          <Close fontSize="small" />
        </IconButton>
      }
      onClick={() => {
        if (!notification.read) onMarkRead(notification.id);
        if (route) onNavigate(route);
      }}
    >
      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
        {severityIcon[notification.severity]}
      </ListItemIcon>
      <ListItemText
        slotProps={{ secondary: { component: 'div' } }}
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: notification.read ? 400 : 600 }}>
              {notification.title}
            </Typography>
          </Box>
        }
        secondary={
          <>
            {notification.description && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                {notification.description}
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 0.25 }}>
              {formatTimestamp(notification.timestamp)}
            </Typography>
          </>
        }
      />
    </ListItem>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ActionCenter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    dismissNotification,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
  } = useActionCenter();

  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton color="inherit" onClick={() => setOpen(true)} title={t('notifications.title')}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <BellLucide size={20} />
        </Badge>
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'transparent' } },
          paper: {
            sx: {
              width: DRAWER_WIDTH,
              display: 'flex',
              flexDirection: 'column',
              top: '59px',
              height: 'calc(100% - 58px)',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('notifications.title')}
            </Typography>
            {unreadCount > 0 && (
              <Chip label={unreadCount} size="small" color="error" />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {notifications.length > 0 && (
              <>
                <Button
                  size="small"
                  startIcon={<DoneAll fontSize="small" />}
                  onClick={markAllAsRead}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {t('notifications.readAll')}
                </Button>
                <Button
                  size="small"
                  startIcon={<DeleteSweep fontSize="small" />}
                  onClick={clearAllNotifications}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {t('notifications.clearAll')}
                </Button>
              </>
            )}
            <IconButton size="small" onClick={() => setOpen(false)} title={t('common.close')}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Notification List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                {t('notifications.noNotifications')}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onDismiss={dismissNotification}
                  onMarkRead={markAsRead}
                  onNavigate={(route) => {
                    setOpen(false);
                    navigate(route);
                  }}
                />
              ))}
            </List>
          )}
        </Box>
      </Drawer>
    </>
  );
}
